import { plainToClass } from "class-transformer";
import { DateUpdatedSection } from "../../../../shared/components/date-updated-section/date-updated-section.view";
import { Appendix } from "../../../../shared/entities/scripts/appendix";
import { Site } from "../../../../shared/entities/scripts/site";
import { Study } from "../../../../shared/entities/scripts/study";
import { DoArraysOverlap, Unique } from "../../../../shared/scripts/array-functions";
import { SortDates } from "../../../../shared/scripts/date-functions";
import { IStudyAppendixSiteFilterParent, StudyAppendixSiteFilterSection } from "../../../../shared/scripts/filtering/filtering.study-appendix-site";
import { ResetDropdownFilters } from "../../../../shared/scripts/filtering/filtering.utilities";
import { FormatNumberWithCommas } from "../../../../shared/scripts/number-functions";
import { ForecastGraphDataPoint } from "../../forecasts/scripts/forecasts.entities";
import { SiteInfo } from "../../sites/shared/sites.entities";
import { ChartController } from "./enrollment.chart-controller";
import { AggregatedParticipants, Participant } from "./enrollment.entities";
import { EnrollmentGauges } from "./enrollment.gauges";
import { EnrollmentLookup } from "./enrollment.lookup";
import { ProgressBarSection } from "./enrollment.progress-bars";

export class EnrollmentPage implements IStudyAppendixSiteFilterParent, IEnrollmentPageData {

    allStudies: Study[] = [];
    allAppendices: Appendix[] = [];

    consentedSites: Site[] = [];
    activatedSites: Site[] = [];
    //siteActivationStudyTimestampMap: Map<number, number[]> = new Map<number, number[]>(); //key: studyId, value: array of site activation timestamps
    sitesActivatedByMonthByStudy: Map<string, number> = new Map<string, number>(); //key = studyId_dateTimestamp, value = count

    allParticipants: Participant[];
    selectedParticipants: Participant[];
    aggregatedParticipants: AggregatedParticipants = new AggregatedParticipants();

    forecastRandomized: ForecastGraphDataPoint[];
    selectedForecastRandomized: ForecastGraphDataPoint[];

    filter: StudyAppendixSiteFilterSection;
    progressBars: ProgressBarSection;
    gauges: EnrollmentGauges;
    chartController: ChartController;

    selectedPopulation: string; //consented, randomized, screen-fail
    selectedBreakdown: string; // "Overall", "Race", "Age", etc...
    selectedAggregation: string; // overall, ByStudy, ByAppendix, BySite
    selectedGraphView: string; //cumulative (line), pie chart, by month (bar), rate by month (bar)
    unassignedAppendixIds: number[] = [];
    showUnassigned: boolean = true;
    unassignedAppendicies: Appendix[] = [];
 
    constructor(siteInfoJson: any, participantJson: any, randomizedAspirationalJson: any, lastUpdatedJson: any) {

        $(() => {

            //console.log(siteInfoJson, participantJson, randomizedAspirationalJson, lastUpdatedJson);

            let foundSiteIds: string[] = [];
            let foundStudyIds: number[] = [];
            let foundAppendixIds: number[] = [];

            this.allParticipants = plainToClass(Participant, <Participant[]>participantJson);
            this.allParticipants = this.allParticipants.filter((p: Participant) => {
                p.initialize();

                if (p.Consented == 1) {
                    if (!foundSiteIds.includes(p.SiteNumber + "-" + p.StudyId)) { foundSiteIds.push(p.SiteNumber + "-" + p.StudyId); }

                    if (!foundStudyIds.includes(p.StudyId)) {
                        this.allStudies.push(new Study(p.StudyId, Study.GetStudyName(p.StudyId)));
                        foundStudyIds.push(p.StudyId);
                    }

                    if (!foundAppendixIds.includes(p.AppendixId)) {
                        if (Appendix.GetAppendixName(p.AppendixId).includes("Unassigned")) {
                            if (!this.unassignedAppendixIds.includes(p.AppendixId)) {
                                this.unassignedAppendixIds.push(p.AppendixId);
                                this.unassignedAppendicies.push(new Appendix(p.AppendixId, p.StudyId, Appendix.GetAppendixName(p.AppendixId)));
                            }
                            return true;
                        }
                        this.allAppendices.push(new Appendix(p.AppendixId, p.StudyId, Appendix.GetAppendixName(p.AppendixId)));
                        foundAppendixIds.push(p.AppendixId);
                    }

                    return true;
                }
                else return false;
            });

            let siteInfo: SiteInfo[] = SiteInfo.Parse(siteInfoJson);
            let consentedSiteMap: Map<number, Site> = new Map<number, Site>();
            let activatedSiteMap: Map<number, Site> = new Map<number, Site>();
            siteInfo.forEach((s: SiteInfo) => {
                if (foundSiteIds.includes(s.SiteNumber + "-" + s.StudyId)) {
                    if (consentedSiteMap.has(s.SiteNumber)) {
                        consentedSiteMap.get(s.SiteNumber).StudyIds.push(s.StudyId);
                    }
                    else {
                        let newSite: Site = new Site(s.SiteNumber, s.getSiteNameNumber());
                        newSite.StudyIds.push(s.StudyId);
                        consentedSiteMap.set(s.SiteNumber, newSite);
                    }
                }

                if (s.ActivationDate != undefined) {
                    s.setActivationTimestamp();
                    if (activatedSiteMap.has(s.SiteNumber)) {
                        activatedSiteMap.get(s.SiteNumber).StudyIds.push(s.StudyId);
                        activatedSiteMap.get(s.SiteNumber).ActivationTimestamps[s.StudyId] = s.ActivationTimestamp;
                    }
                    else {
                        let newSite: Site = new Site(s.SiteNumber, s.getSiteNameNumber());                        
                        newSite.StudyIds.push(s.StudyId);
                        newSite.ActivationTimestamps[s.StudyId] = s.ActivationTimestamp;
                        activatedSiteMap.set(s.SiteNumber, newSite);
                    }
                }
            });
            this.consentedSites = Array.from(consentedSiteMap.values()).sort((a: Site, b: Site) => a.Name.localeCompare(b.Name));
            this.activatedSites = Array.from(activatedSiteMap.values()).sort((a: Site, b: Site) => a.Name.localeCompare(b.Name));

            //console.log(this.consentedSites);
            //console.log(activatedSiteMap);
            //console.log(this.activatedSites);

            this.forecastRandomized = plainToClass(ForecastGraphDataPoint, <ForecastGraphDataPoint[]>randomizedAspirationalJson);            
            this.forecastRandomized = this.forecastRandomized.map((point: ForecastGraphDataPoint) => {
                point.initialize("first", "count");
                return point;
            }).filter((x: ForecastGraphDataPoint) => x.TypeId == 2 && x.IsOverall == 0); //only appendix-level as we aggregate
            this.selectedForecastRandomized = this.forecastRandomized;

            //console.log(this.sites);
            //console.log(this.participants);
            //console.log(this.studies);
            //console.log(this.appendices);
            //console.log(this.forecastRandomized);

            new DateUpdatedSection(lastUpdatedJson, false, true, false);

            this.changeSelectedPopulation("randomized", false);
            this.changeSelectedBreakdown("All Participants", '', false);
            this.changeSelectedAggregation("Overall", false);
            this.changeSelectedGraphView("Cumulative", false);

            this.progressBars = new ProgressBarSection();
            this.gauges = new EnrollmentGauges(this);
            this.chartController = new ChartController(this);

            //this.allAppendices.sort((a1: Appendix, a2: Appendix) => {
            //    return a1.Name.localeCompare(a2.Name);
            //});

            this.filter = new StudyAppendixSiteFilterSection("#study-dropdown", "#appendix-dropdown", "#site-dropdown", this.allStudies, this.allAppendices, this.consentedSites, this);

            $('#loading-indicator').hide();
            $('.loaded-content').removeClass('invisible');
            this.hideUnassignedToggleButton();
        });
    }

    changeSelectedPopulation(value: string, triggerRefresh: boolean = true): void {

        this.selectedPopulation = value;

        //need to change sorting for graphing
        if (value == "consented") {
            this.allParticipants = this.allParticipants.sort((p1, p2) => SortDates(p1.DateConsented, p2.DateConsented));
            $('#label-population').text("Consented");
            this.showUnassignedToggleButton();
        }
        else if (value == "randomized") {
            this.allParticipants = this.allParticipants.sort((p1, p2) => SortDates(p1.DateRandomized, p2.DateRandomized));
            $('#label-population').text("Randomized");
            this.hideUnassignedToggleButton();
        }
        else if (value == "screen-failed") {
            this.allParticipants = this.allParticipants.sort((p1, p2) => SortDates(p1.DateConsented, p2.DateConsented));
            $('#label-population').text("Screen Failed");
            this.showUnassignedToggleButton();
        }

        //console.log(this.participants);

        if (triggerRefresh) { this.filter?.refreshFilters(); }
    }

    changeSelectedBreakdown(newBreakdown: string, selectedDomainId: string = "", triggerRefresh: boolean = true): void {
        this.selectedBreakdown = newBreakdown;

        $('#chart-view-label').text((newBreakdown == "All Participants" ? newBreakdown : ("By " + newBreakdown)));
        $('.chart-details-toggle-button').removeClass('selected');

        let activateStudies: boolean = true;
        let activateAppendices: boolean = true;
        let activateSites: boolean = true;
        if (this.selectedBreakdown == "All Participants") {
            $('#view-all-no-breakdown').hide();

            //if (this.selectedGraphView == "Proportional" && this.selectedAggregation == "Overall") {
            //    this.changeSelectedAggregation("ByStudy", false);
            //}
            //else if (this.selectedGraphView == "RatePerSiteByMonth") { activateSites = false; }
            if (this.selectedGraphView == "RatePerSiteByMonth") { activateSites = false; }
            this.enableSelectionOption('#enrollment-graph-view', "RatePerSiteByMonth", true);
        }
        else
        {
            $('#view-all-no-breakdown').show();
            $('#' + selectedDomainId).addClass('selected');
            activateStudies = activateAppendices = activateSites = false;

            if (this.selectedAggregation != "Overall") { this.changeSelectedAggregation("Overall", false); }
            if (this.selectedGraphView == "RatePerSiteByMonth") { $('#enrollment-graph-view').val("Cumulative").trigger('change'); }
            this.enableSelectionOption('#enrollment-graph-view', "RatePerSiteByMonth", false);
        }
        this.enableButton('#view-studies-button', activateStudies);
        this.enableButton('#view-appendices-button', activateAppendices);
        this.enableButton('#view-sites-button', activateSites);

        if (triggerRefresh) { this.chartController.UpdateData(); }
    }

    changeSelectedAggregation(newAggregation: string, triggerRefresh: boolean = true): void {
        this.selectedAggregation = newAggregation;

        $('.aggregation-option').removeClass('active');
        if (this.selectedAggregation == "Overall") { $('#view-overall-button').addClass('active'); }
        if (this.selectedAggregation == "ByStudy") { $('#view-studies-button').addClass('active'); }
        if (this.selectedAggregation == "ByAppendix") { $('#view-appendices-button').addClass('active'); }
        if (this.selectedAggregation == "BySite") { $('#view-sites-button').addClass('active'); }

        if (triggerRefresh) { this.chartController.UpdateData(); }
    }

    changeSelectedGraphView(newGraphView: string, triggerRefresh: boolean = true): void {

        this.selectedGraphView = newGraphView;

        if (this.selectedGraphView == "Cumulative" || this.selectedGraphView == "ByMonth" || this.selectedGraphView == "RatePerSiteByMonth") {
            let monthField: string = "";
            let countField: string = "";
            if (this.selectedGraphView == "Cumulative") { monthField = "last"; countField = "count"; }
            else if (this.selectedGraphView == "ByMonth") { monthField = "first"; countField = "count"; }
            else if (this.selectedGraphView == "RatePerSiteByMonth") { monthField = "first"; countField = "rate"; }

            this.forecastRandomized.forEach((x: ForecastGraphDataPoint) => { x.DayOfMonth = monthField; x.CountField = countField; });
            this.selectedForecastRandomized.forEach((x: ForecastGraphDataPoint) => { x.DayOfMonth = monthField; x.CountField = countField; });
        }
        else if (this.selectedGraphView == "Proportional") {
            //if (this.selectedAggregation == "Overall" && this.selectedBreakdown == "All Participants") {
            //    this.changeSelectedAggregation("ByStudy", false);
            //}
        }

        if (this.selectedGraphView == "RatePerSiteByMonth") {
            if (this.selectedAggregation == "BySite") { this.changeSelectedAggregation("Overall", false); }
            this.enableButton('#view-sites-button', false);
        }
        else if (this.selectedBreakdown == "All Participants") { this.enableButton('#view-sites-button', true); }

        if (triggerRefresh) { this.chartController.UpdateData(); }
    }


    FilterUpdated(studyIds: number[], appendixIds: number[], siteIds: number[]) {
        //console.log("FilterUpdated", studyIds, appendixIds, siteIds);

        this.selectedParticipants = this.allParticipants.filter((p: Participant) => {
            if (this.selectedPopulation == "consented" && p.Consented != 1) return false;
            else if (this.selectedPopulation == "randomized" && p.Randomized != 1) return false;
            else if (this.selectedPopulation == "screen-failed" && p.ScreenFail != 1) return false;

            if (!studyIds.includes(p.StudyId)) return false;
            if (this.showUnassigned) {
                appendixIds = appendixIds.concat(this.unassignedAppendixIds);
            }
            if (!appendixIds.includes(p.AppendixId)) return false;
            if (!siteIds.includes(p.SiteNumber)) return false;

            return true;
        });

        //determine how many sites were activated for each study by month for monthly-reporting - only looking at select participants
        let monthTimestamps: number[] = [];
        this.selectedParticipants.forEach((p: Participant) => {
            if (this.selectedPopulation == "consented" || this.selectedPopulation == "screen-failed") {
                if (!monthTimestamps.includes(p.MonthConsented.getTime())) { monthTimestamps.push(p.MonthConsented.getTime()); }
            }
            else if (this.selectedPopulation == "randomized") {
                if (!monthTimestamps.includes(p.MonthRandomized.getTime())) { monthTimestamps.push(p.MonthRandomized.getTime()); }
            }
        })

        this.sitesActivatedByMonthByStudy.clear();
        let subsetOfSites: boolean = !siteIds.includes(0);
        this.activatedSites.forEach((s: Site) => {
            if (!DoArraysOverlap(s.StudyIds, studyIds)) return;
            if (subsetOfSites && !siteIds.includes(s.Id)) return;

            studyIds.forEach((studyId: number) => {
                monthTimestamps.forEach((monthTimestamp: number) => {
                    if (s.ActivationTimestamps[studyId] != undefined && s.ActivationTimestamps[studyId] <= monthTimestamp) {
                        let key: string = studyId + "_" + monthTimestamp;
                        let count: number = 0;
                        if (this.sitesActivatedByMonthByStudy.has(key)) {
                            count = this.sitesActivatedByMonthByStudy.get(key);
                        }
                        count += 1;
                        this.sitesActivatedByMonthByStudy.set(key, count);
                        //console.log(studyId + " | " + monthTimestamp + " | " + new Date(monthTimestamp) + " | " + count + " -- " + s.Id);
                        //if (monthTimestamp == 1704128400000 && studyId == 3) console.log(s.Id);
                    }
                });
            });            
        });
        //console.log(this.sitesActivatedByMonthByStudy);

        //forecasted information
        if (this.selectedPopulation == "randomized") {
            if (!siteIds.includes(0)) { this.selectedForecastRandomized = []; }
            else {
                this.selectedForecastRandomized = this.forecastRandomized.filter((x: ForecastGraphDataPoint) => appendixIds.includes(x.AppendixId));
            }
        }
        else { this.selectedForecastRandomized = []; }

        this.aggregatedParticipants.aggregate(this.selectedParticipants);
        //console.log(this.aggregatedParticipants);

        this.progressBars.updateProgressBarData(this.aggregatedParticipants);

        //console.log(studyIds);
        //if more than VITAL is selected, hide Symptom Clusters
        if (studyIds.length == 1 && studyIds[0] == 2 && this.selectedPopulation == "randomized") {
            //$('#sxcluster-exint').show(); $('#sxcluster-cogdys').show(); $('#sxcluster-autonomic').show();
            $('#symptom-cluster-container').show();
            this.gauges.UpdateData(this.aggregatedParticipants);
            $('#sxcluster-randomized-label').text(FormatNumberWithCommas(String(this.selectedParticipants.length)));

            let percent: number = (this.selectedParticipants.length / 900) * 100;
            $('#sxcluster-percent-label').text(FormatNumberWithCommas(String(Math.round(percent))));
        }
        else {
            //$('#sxcluster-exint').hide(); $('#sxcluster-cogdys').hide(); $('#sxcluster-autonomic').hide();
            //if (this.selectedBreakdown.startsWith("SX Cluster")) {
            //    this.changeSelectedBreakdown("All Participants", "", false); //to make sure when SymClusters are hidden, if selected, not shown anymore
            //}
            $('#symptom-cluster-container').hide();
        }

        $('#value-total-population').text(FormatNumberWithCommas(String(this.selectedParticipants.length)));
               
        this.chartController.UpdateData();
    }

    resetFilters(): void {
        this.showUnassigned = false;
        (document.getElementById("unassigned-switch") as HTMLInputElement).checked = false;
        $('#population-select').val("randomized");
        $('#enrollment-graph-view').val("Cumulative");

        this.changeSelectedPopulation("randomized", true);
        this.changeSelectedBreakdown("All Participants", '', true);
        this.changeSelectedAggregation("Overall", true);

        ResetDropdownFilters(['#study-dropdown', '#appendix-dropdown', '#site-dropdown']);

        this.changeSelectedGraphView('Cumulative', true);
    }

    toggleUnassignedAppendices(): void {
        this.showUnassigned = !this.showUnassigned;
        this.FilterUpdated(this.filter.selectedStudyIds, this.filter.selectedAppendixIds, this.filter.selectedSiteIds);
    }

    private enableButton(buttonId: string, active: boolean): void {
        $(buttonId).prop("disabled", !active);
        if (!active) { $(buttonId).addClass("disabled"); }
        else { $(buttonId).removeClass("disabled"); }
    }

    private enableSelectionOption(selectId: string, value: string, active: boolean): void {
        $(selectId + ' option').each(function () {
            if ($(this).val() == value) {
                if (active) { $(this).removeAttr('disabled'); }
                else { $(this).attr('disabled', 'disabled'); }                
                return;
            }
        });

        $(selectId).selectpicker('refresh');
    }

    showUnassignedToggleButton(): void {
        $('#unassigned-toggle').show();
        $('#reset-filters').removeClass("ml-auto");
    }

    hideUnassignedToggleButton(): void {
        $('#unassigned-toggle').hide();
        $('#reset-filters').addClass("ml-auto");
    }

    /*
    updateGraphView(viewName: string, chartViewButtonId: string, type: string, aggregation: string): void {
        
        this.dataToView = viewName;
        this.currentSelectedChartViewId = chartViewButtonId;
        this.currentChartType = type;
        this.aggregationView = aggregation;

        //$('#chart-view-label').text('(' + viewName + ')');
        $('#chart-view-label').text((viewName == "All Participants" ? viewName : ("By " + viewName) ));

        if (type == "line-bar") {
            $('#piechart-display').hide();
            $('#accrual-display').show();
            //$('#label-details-heading').text("Accrual History by");
            $('#enrollment-graph-view').show();            
        }
        else if(type == "pie") {
            $('#accrual-display').hide();
            $('#piechart-display').show();
            //$('#label-details-heading').text("Accrual by");
            $('#enrollment-graph-view').hide();

        //    this.forecastRandomized.forEach((x: ForecastGraphDataPoint) => x.DayOfMonth = "first");
        //    this.selectedForecastRandomized.forEach((x: ForecastGraphDataPoint) => x.DayOfMonth = "first");
        }

        $('.chart-details-toggle-button').removeClass('selected');

        if (chartViewButtonId == "chart-view-all") { $('#chart-view-all').hide(); }
        else {
            $('#chart-view-all').show();
            $('#' + chartViewButtonId).addClass('selected');
        }

        if (type == 'line-bar') {

            this.accrualChart.UpdateData(this.selectedParticipants, this.dataToView, this.selectedForecastRandomized, this.aggregationView, this.graphDataType, this.selectedPopulation, this.allStudies, this.allAppendices, this.consentedSites, this.selectedActivatedSiteTimestamps);

            if (viewName == "All Participants") {
                $('#view-overall-button').addClass('active');
                $('#view-studies-button').prop("disabled", false);
                $('#view-appendices-button').prop("disabled", false);
                $('#view-sites-button').prop("disabled", false);
                $('#view-studies-button').removeClass("disabled");
                $('#view-appendices-button').removeClass("disabled");
                $('#view-sites-button').removeClass("disabled");
            }
            else {
                $('#view-overall-button').addClass('active');
                $('#view-studies-button').removeClass('active');
                $('#view-appendices-button').removeClass('active');
                $('#view-sites-button').removeClass('active');
                $('#view-studies-button').prop("disabled", true);
                $('#view-appendices-button').prop("disabled", true);
                $('#view-sites-button').prop("disabled", true);
                $('#view-studies-button').addClass("disabled");
                $('#view-appendices-button').addClass("disabled");
                $('#view-sites-button').addClass("disabled");
            }
        }
        else if (type == 'pie') {
            let data: number[] = [];
            if (viewName == "RECOVER Cohort") { data = this.aggregatedParticipants.RecoverCohort; }
            else if (viewName == "Age") { data = this.aggregatedParticipants.Age; }
            else if (viewName == "Race") { data = this.aggregatedParticipants.Race; }
            else if (viewName == "Ethnicity") { data = this.aggregatedParticipants.Hispanic; }
            else if (viewName == "Non-White or Hispanic") { data = this.aggregatedParticipants.NonWhiteHispanic; }
            else if (viewName == "Sex") { data = this.aggregatedParticipants.Sex; }
            else if (viewName == "SX Cluster: ExInt (Enrolled)") { data = this.aggregatedParticipants.ClusterExInt; }
            else if (viewName == "SX Cluster: CogDys (Enrolled)") { data = this.aggregatedParticipants.ClusterCogn; }
            else if (viewName == "SX Cluster: Autonomic (Enrolled)") { data = this.aggregatedParticipants.ClusterAuto; }
            
            this.pieChart.UpdateData(viewName, data);
            //console.log(viewName);
        }

        this.changeChartAggregation(this.aggregationView);
    }

    changeChartAggregation(selectedView: string): void {

        this.aggregationView = selectedView;
        
        this.accrualChart.UpdateData(this.selectedParticipants, this.dataToView, this.selectedForecastRandomized, selectedView, this.graphDataType, this.selectedPopulation, this.allStudies, this.allAppendices, this.consentedSites, this.selectedActivatedSiteTimestamps);

        $('#view-overall-button').removeClass('active');
        $('#view-studies-button').removeClass('active');
        $('#view-appendices-button').removeClass('active');
        $('#view-sites-button').removeClass('active');
        if (selectedView == "Overall") { $('#view-overall-button').addClass('active'); }
        if (selectedView == "ByStudy") { $('#view-studies-button').addClass('active'); }
        if (selectedView == "ByAppendix") { $('#view-appendices-button').addClass('active'); }
        if (selectedView == "BySite") { $('#view-sites-button').addClass('active'); }
    }

    graphDataTypeChanged(newType: any): void {
        this.graphDataType = newType;

        let dayOfMonth: string = "last";
        let countField: string = "count";

        if (this.graphDataType == "Cumulative") {
            $('.bar-chart-icon').hide();
            $('.line-chart-icon').show();
        }
        else if (this.graphDataType == "ByMonth" || this.graphDataType == "RatePerSiteByMonth") {
            $('.line-chart-icon').hide();
            $('.bar-chart-icon').show();
            dayOfMonth = "first";
            if (this.graphDataType == "RatePerSiteByMonth") countField = "rate";
        }

        this.forecastRandomized.forEach((x: ForecastGraphDataPoint) => { x.DayOfMonth = dayOfMonth; x.CountField = countField; });
        this.selectedForecastRandomized.forEach((x: ForecastGraphDataPoint) => { x.DayOfMonth = dayOfMonth; x.CountField = countField; });

        this.accrualChart.UpdateData(this.selectedParticipants, this.dataToView, this.selectedForecastRandomized, this.aggregationView, this.graphDataType, this.selectedPopulation, this.allStudies, this.allAppendices, this.consentedSites, this.selectedActivatedSiteTimestamps);
    }
    */
}

export interface IEnrollmentPageData {
    allStudies: Study[];
    allAppendices: Appendix[];

    consentedSites: Site[];
    activatedSites: Site[];
    //siteActivationStudyTimestampMap: Map<number, number[]>;
    sitesActivatedByMonthByStudy: Map<string, number>; //key = studyId_dateTimestamp, value = count

    //allParticipants: Participant[];
    selectedParticipants: Participant[];
    aggregatedParticipants: AggregatedParticipants;

    //forecastRandomized: ForecastGraphDataPoint[];
    selectedForecastRandomized: ForecastGraphDataPoint[];

    selectedPopulation: string; //consented, randomized, screen-fail
    selectedBreakdown: string; // "Overall", "Race", "Age", etc...
    selectedAggregation: string; // overall, ByStudy, ByAppendix, BySite
    selectedGraphView: string; //cumulative (line), pie chart, by month (bar), rate by month (bar)
    unassignedAppendixIds: number[];
    showUnassigned: boolean;
    unassignedAppendicies: Appendix[];

}